import { useEffect, useMemo, useState } from "react";
import {
  FaBoxes,
  FaClipboardList,
  FaDownload,
  FaIndustry,
  FaRedo,
  FaReceipt,
  FaShoppingCart,
  FaTasks,
  FaTruck,
} from "react-icons/fa";
import { reportAPI } from "../api/reportAPI";
import "./MasterReport.css";

const numberFormatter = new Intl.NumberFormat("en-IN");
const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const iconMap = {
  sales: FaShoppingCart,
  purchases: FaReceipt,
  manufacture: FaIndustry,
  tasks: FaTasks,
  rtoDto: FaTruck,
  inventory: FaBoxes,
  packaging: FaBoxes,
  returns: FaTruck,
  settlement: FaClipboardList,
  expenses: FaReceipt,
};

const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const todayKey = () => new Date().toISOString().slice(0, 10);

const getPresetRange = (preset) => {
  const today = new Date();
  const toDate = today.toISOString().slice(0, 10);
  if (preset === "today") return { fromDate: toDate, toDate };
  if (preset === "7days" || preset === "30days") {
    const start = new Date(today);
    start.setDate(today.getDate() - (preset === "7days" ? 6 : 29));
    return { fromDate: start.toISOString().slice(0, 10), toDate };
  }
  return { fromDate: "", toDate: "" };
};

const formatValue = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "number") return formatNumber(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(value))) return String(value).slice(0, 10);
  return String(value);
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadSectionCsv = (section, filters) => {
  const rows = Array.isArray(section.recent) ? section.recent : [];
  const fieldSet = new Set();
  rows.forEach((row) => Object.keys(row || {}).forEach((key) => fieldSet.add(key)));
  const fields = Array.from(fieldSet);
  const safeTitle = String(section.title || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const datePart = filters.fromDate || filters.toDate
    ? `${filters.fromDate || "start"}-to-${filters.toDate || "today"}`
    : new Date().toISOString().slice(0, 10);
  const fileName = `${safeTitle || "master-report"}-${datePart}.csv`;

  const summaryRows = [
    ["Report", section.title],
    ["Total", section.total || 0],
    ["Today", section.today || 0],
    ["Quantity", section.quantity || 0],
    ["Value", section.amount || 0],
  ];

  const statusRows = Array.isArray(section.extra?.status)
    ? section.extra.status.map((row) => [`Status: ${row.label}`, row.value])
    : [];

  const lines = [
    ...summaryRows.map((row) => row.map(csvEscape).join(",")),
    ...statusRows.map((row) => row.map(csvEscape).join(",")),
    "",
    fields.length ? fields.map(csvEscape).join(",") : csvEscape("No records found"),
    ...rows.map((row) => fields.map((field) => csvEscape(row?.[field])).join(",")),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export default function MasterReport() {
  const [overview, setOverview] = useState({ totals: {}, sections: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    reportType: "all",
    datePreset: "all",
    fromDate: "",
    toDate: "",
    search: "",
  });

  const loadOverview = async (nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const data = await reportAPI.getMasterOverview({
        fromDate: nextFilters.fromDate,
        toDate: nextFilters.toDate,
        search: nextFilters.search,
      });
      setOverview({
        totals: data?.totals || {},
        sections: Array.isArray(data?.sections) ? data.sections : [],
        generatedAt: data?.generatedAt,
      });
    } catch (err) {
      console.error("Failed to load master report:", err);
      setError(err?.response?.data?.message || "Failed to load master report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [filters.fromDate, filters.toDate, filters.search]);

  const reportOptions = useMemo(
    () => overview.sections.map((section) => ({ key: section.key, title: section.title })),
    [overview.sections]
  );

  const visibleSections = useMemo(() => {
    if (filters.reportType === "all") return overview.sections;
    return overview.sections.filter((section) => section.key === filters.reportType);
  }, [overview.sections, filters.reportType]);

  const visibleTotals = useMemo(
    () => ({
      records: visibleSections.reduce((sum, section) => sum + Number(section.total || 0), 0),
      today: visibleSections.reduce((sum, section) => sum + Number(section.today || 0), 0),
      amount: visibleSections.reduce((sum, section) => sum + Number(section.amount || 0), 0),
      reportTypes: visibleSections.length,
    }),
    [visibleSections]
  );

  const updateDatePreset = (value) => {
    const range = getPresetRange(value);
    setFilters((prev) => ({
      ...prev,
      datePreset: value,
      fromDate: value === "custom" ? prev.fromDate : range.fromDate,
      toDate: value === "custom" ? prev.toDate : range.toDate,
    }));
  };

  const resetFilters = () => {
    setFilters({
      reportType: "all",
      datePreset: "all",
      fromDate: "",
      toDate: "",
      search: "",
    });
  };

  const topCards = useMemo(
    () => [
      { label: "Total Records", value: formatNumber(visibleTotals.records) },
      { label: "Today Records", value: formatNumber(visibleTotals.today) },
      { label: "Total Value", value: formatCurrency(visibleTotals.amount) },
      { label: "Report Types", value: formatNumber(visibleTotals.reportTypes) },
    ],
    [visibleTotals]
  );

  return (
    <div className="master-report page-container">
      <header className="master-report-hero">
        <div>
          <span className="master-report-eyebrow">Reports</span>
          <h1>Master Report</h1>
          <p>Sales, purchase, manufacture, tasks, RTO/DTO aur baaki modules ka live overview.</p>
        </div>
        <button type="button" className="master-report-refresh" onClick={() => loadOverview()} disabled={loading}>
          <FaRedo /> {loading ? "Loading" : "Refresh"}
        </button>
      </header>

      {error ? <div className="master-report-alert">{error}</div> : null}

      <section className="master-report-filter-card">
        <label>
          <span>Report Type</span>
          <select value={filters.reportType} onChange={(event) => setFilters((prev) => ({ ...prev, reportType: event.target.value }))}>
            <option value="all">All Reports</option>
            {reportOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.title}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Date</span>
          <select value={filters.datePreset} onChange={(event) => updateDatePreset(event.target.value)}>
            <option value="all">All History</option>
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="custom">Custom Date</option>
          </select>
        </label>
        <label>
          <span>From</span>
          <input
            type="date"
            value={filters.fromDate}
            max={filters.toDate || todayKey()}
            onChange={(event) => setFilters((prev) => ({ ...prev, datePreset: "custom", fromDate: event.target.value }))}
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="date"
            value={filters.toDate}
            min={filters.fromDate || undefined}
            max={todayKey()}
            onChange={(event) => setFilters((prev) => ({ ...prev, datePreset: "custom", toDate: event.target.value }))}
          />
        </label>
        <label>
          <span>Search</span>
          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Order, SKU, product..."
          />
        </label>
        <button type="button" className="master-report-clear" onClick={resetFilters}>Clear</button>
      </section>

      <section className="master-report-top-grid">
        {topCards.map((card) => (
          <article key={card.label} className="master-report-total-card">
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </section>

      <section className="master-report-grid">
        {(loading ? [] : visibleSections).map((section) => {
          const Icon = iconMap[section.key] || FaClipboardList;
          const statusRows = Array.isArray(section.extra?.status) ? section.extra.status.slice(0, 4) : [];
          const recentRows = Array.isArray(section.recent) ? section.recent : [];

          return (
            <article key={section.key} className="master-report-card">
              <div className="master-report-card-head">
                <span className="master-report-icon"><Icon /></span>
                <div>
                  <h2>{section.title}</h2>
                  <p>{formatNumber(section.today)} today</p>
                </div>
                <button
                  type="button"
                  className="master-report-download"
                  onClick={() => downloadSectionCsv(section, filters)}
                  title={`Download ${section.title}`}
                >
                  <FaDownload />
                  CSV
                </button>
              </div>

              <div className="master-report-metrics">
                <div>
                  <span>Total</span>
                  <strong>{formatNumber(section.total)}</strong>
                </div>
                <div>
                  <span>Qty</span>
                  <strong>{formatNumber(section.quantity)}</strong>
                </div>
                <div>
                  <span>Value</span>
                  <strong>{formatCurrency(section.amount)}</strong>
                </div>
              </div>

              {section.extra?.awb ? (
                <div className="master-report-awb">
                  AWB: {formatNumber(section.extra.awb.unique)} unique, {formatNumber(section.extra.awb.today)} today
                </div>
              ) : null}

              <div className="master-report-status-list">
                {statusRows.length ? (
                  statusRows.map((row) => (
                    <span key={`${section.key}-${row.label}`}>
                      {row.label}: <b>{formatNumber(row.value)}</b>
                    </span>
                  ))
                ) : (
                  <span>No status split</span>
                )}
              </div>

              <div className="master-report-recent">
                <h3>Records</h3>
                {recentRows.length ? (
                  recentRows.slice(0, 4).map((row, index) => (
                    <div key={`${section.key}-recent-${index}`} className="master-report-row">
                      {Object.entries(row).slice(0, 4).map(([key, value]) => (
                        <span key={key}>
                          <small>{key.replace(/_/g, " ")}</small>
                          <b>{formatValue(value)}</b>
                        </span>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="master-report-empty">No records found.</div>
                )}
              </div>
            </article>
          );
        })}

        {loading ? (
          <div className="master-report-loading">
            <div />
            <strong>Master report loading...</strong>
          </div>
        ) : null}
      </section>
    </div>
  );
}
